import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FEATURE_FLAG } from '../../../../../constants/general';
import Spinner, { SpinnerColor } from '../../../../../generic-components/Spinner';
import { useAccountSummaryQuery } from '../../../../../hooks/query/useAccountSummaryQuery';
import { useGetUncategorizedData } from '../../../../../hooks/query/useGetUncategorizedTransactionsDataForScheduleCQuery';
import { useTaxEstimateDataQuery } from '../../../../../hooks/query/useTaxEstimateDataQuery';
import { validateFeatureFlag } from '../../../../../services/featureFlagService';
import { trackMixPanel } from '../../../../../services/mixpanel.service';
import { useStore } from '../../../../../store';
import { ITransaction, IUncategorizedTransactions } from '../../../../../types';
import { LiliTransaction } from '../../../../TransactionsFacelift/types/lili-transaction';
import { ACTION_TYPES } from '../../../consts';
import { CategorizationContext } from '../../../contexts/CategorizationContext';
import { CategorizationTypes } from '../../../enums';
import { useCategorizationHook } from '../../../hooks/useCategorizationHook';
import { IActionTypes } from '../../../types';
import {
  getBusinessPercentByActionType,
  getCategoryForTransaction,
  isCategoryChosen,
} from '../../../utils/categorization.utils';
import { CategorizationAllDone } from './CategorizationAllDone';
import { CategorizationBusinessActions } from './CategorizationBusinessActions';
import { CategorizationCard } from './CategorizationCard';
import { CategorizationPersonalActions } from './CategorizationPersonalActions';
import { CategorizationTypeSwitcher } from './CategorizationTypeSwitcher';
import { WidgetModal } from './WidgetModal';
import { WidgetSplitTransaction } from './WidgetSplitTransaction';
import { VIEWS_MAP } from '../../../../../router/constants';
import { CategorizeCreditCardWarningModalDashboard } from '../../../../TransactionsFacelift/components/sync-your-card/CategorizeCreditCardWarningModalDashboard';
import { useLinkedAccounts } from '../../../../TransactionsFacelift/hooks/useLinkedAccounts';
import { CategorizeCreditCardWarningModal } from '../../../../TransactionsFacelift/components/sync-your-card/CategorizeCreditCardWarningModal';
import { TransactionTabs } from '../../../../TransactionsFacelift/state/createTransactionSlice';
import { useGetSingleInAppComponentLog } from '../../../../../hooks/utilities/useGetSingleInAppComponentLog';
import { InAppComponent } from '../../../../../hooks/query/useGetInappComponentsLogs';

const CREDIT_CARD_CATEGORY_ID = '68CB366D-3AD8-4D8E-93EB-CFD90AAA8DDE';
interface ICategorizationMainViewProps {
  expenseTransactionsList: ITransaction[];
  incomeTransactionsList: ITransaction[];
  numOfTotalExpenses: number;
  numOfTotalIncome: number;
}
export const CategorizationMainView = ({
  expenseTransactionsList,
  incomeTransactionsList,
  numOfTotalExpenses,
  numOfTotalIncome,
}: ICategorizationMainViewProps) => {
  const { customerId } = useStore((state) => state.user);
  const {
    categorizedExpensesCounter,
    setCategorizedExpensesCounter,
    categorizedIncomeCounter,
    setCategorizedIncomeCounter,
  } = useContext(CategorizationContext);
  const setActiveDrawerTransaction = useStore((state) => state.setActiveDrawerTransaction);
  const selectedCategoryDetails = useStore((state) => state.selectedCategoryDetails);
  const setSelectedCategoryDetails = useStore((state) => state.setSelectedCategoryDetails);
  const setTaxSavingAmount = useStore((state) => state.setTaxSavingAmount);
  const actionFlags = useStore((state) => state.summary.actionFlags);
  const { categorizeAction, expenseCategories, incomeCategories } = useCategorizationHook(customerId);
  const [businessPercent, setBusinessPercent] = useState<number>(-1); //
  const [activeCatType, setActiveCatType] = useState<CategorizationTypes.expense | CategorizationTypes.income>(
    CategorizationTypes.expense,
  );
  const [expenseList, setExpenseList] = useState<ITransaction[]>(expenseTransactionsList || []);
  const [incomeList, setIncomeList] = useState<ITransaction[]>(incomeTransactionsList || []);
  const [activeList, setActiveList] = useState<ITransaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCategoryTooltip, setShowCategoryTooltip] = useState<boolean>(false);
  const [showSplitModal, setShowSplitModal] = useState<boolean>(false);
  const firstCard = useRef<HTMLDivElement | null>(null);
  const secondCard = useRef<HTMLDivElement | null>(null);
  const actionType = useRef<IActionTypes>(null);
  const isExpensesTabActive = activeCatType === CategorizationTypes.expense;
  const isIncomeTabActive = activeCatType === CategorizationTypes.income;
  const isExpenseEmpty = numOfTotalExpenses - categorizedExpensesCounter === 0;
  const isIncomeEmpty = numOfTotalIncome - categorizedIncomeCounter === 0;
  const isExpenseEmptyAndActive = isExpensesTabActive && numOfTotalExpenses - categorizedExpensesCounter === 0;
  const isIncomeEmptyAndActive = isIncomeTabActive && numOfTotalIncome - categorizedIncomeCounter === 0;
  const qsForUncategorizedFetch = useMemo(
    () =>
      new URLSearchParams({
        categorizationType: activeCatType,
      }),
    [activeCatType],
  );
  const { refetch: mutateSummary } = useAccountSummaryQuery();
  const { refetch: fetchTaxSaving } = useTaxEstimateDataQuery({ customerId });
  const {
    refetch: fetchUncategorizedData,
    isRefetching: isRefetchingData,
    isLoading: isLoadingData,
  } = useGetUncategorizedData({
    customerId,
    q: qsForUncategorizedFetch.toString(),
    onSuccess: (data: IUncategorizedTransactions) => {
      if (isExpensesTabActive) setExpenseList(data?.transactionList);
      else if (isIncomeTabActive) setIncomeList(data?.transactionList);
    },
  });
  const [activeTransaction, setActiveTransaction] = useState<ITransaction>(activeCatType === CategorizationTypes.expense ? expenseList?.[0] : incomeList?.[0]);
  const previousActiveTransactionDetails = useRef<string>('');
  const activeCategoryId = selectedCategoryDetails?.categoryId
    || activeTransaction?.categoryDetails?.categoryExternalId
    || activeTransaction?.suggestedTaxCategoryId;
  const isLoadingMoreUncategorized = isRefetchingData || isLoadingData;
  const showEmptyState = (isExpenseEmptyAndActive || isIncomeEmptyAndActive) && !isLoadingMoreUncategorized;
  const showIncomeList = validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.INCOME);
  const setShowWarningCategoryModal = useStore((state) => state.setShowWarningCategoryModal);
  const setShowSyncCardModal = useStore((state) => state.setShowSyncCardModal);
  const setSelectedTransactionTab = useStore((state) => state.setSelectedTransactionTab);
  const showWarningCategoryModal = useStore((state) => state.showWarningCategoryModal);
  const shouldShowSyncCardFeature = validateFeatureFlag(FEATURE_FLAG.SYNC_CARD);
  const { selectedSyncedCard, setSelectedSyncedCard } = useStore((state) => state);
  const { data: linkedAccounts = [] } = useLinkedAccounts((linkedAccountsRes) => {
    if (linkedAccountsRes.length > 0 && !selectedSyncedCard) {
      setSelectedSyncedCard(linkedAccountsRes[0]);
    }
  });
  const navigate = useNavigate();
  const {
    data: appComponentLog,
    isLoading: isLoadingAppComponent,
    refetch: refetchLogComponent,
  } = useGetSingleInAppComponentLog(customerId, InAppComponent.LINKED_CARD_CATEGORIZE_MODAL);

  const animateCardSwipe = (animationClass: string) => {
    firstCard.current?.classList.add(animationClass);
  };

  const resetBusinessPercent = () => {
    setBusinessPercent(-1);
  };

  const updateLocalState = () => {
    if (activeCatType === CategorizationTypes.expense) {
      setActiveTransaction(expenseList?.[0]);
      setExpenseList(expenseList?.slice(1));
      setCategorizedExpensesCounter((c) => c + 1);
    }
    if (activeCatType === CategorizationTypes.income) {
      setActiveTransaction(incomeList?.[0]);
      setIncomeList(incomeList?.slice(1));
      setCategorizedIncomeCounter((c) => c + 1);
    }
    actionType.current = null;
  };
  const fetchAndUpdateTaxSavings = async () => {
    const taxSavingResponse = await fetchTaxSaving();
    if (taxSavingResponse) setTaxSavingAmount(taxSavingResponse?.data?.taxEstimate?.taxSaving);
  };
  const handleMutation = async () => {
    try {
      previousActiveTransactionDetails.current = activeTransaction.details;
      const activeBusinessPercent = actionType.current === ACTION_TYPES.SPLIT ? businessPercent : getBusinessPercentByActionType(actionType.current);
      await categorizeAction(activeTransaction, activeCategoryId, activeBusinessPercent);
      resetBusinessPercent();

      if (activeCategoryId === CREDIT_CARD_CATEGORY_ID) {
        setShowWarningCategoryModal(true);
      }
      updateLocalState();
      setSelectedCategoryDetails(null);

      if (!actionFlags?.didCategorizeAnyTransaction) {
        setTimeout(() => {
          mutateSummary();
        }, 300);
      }
      if (activeBusinessPercent !== null && activeBusinessPercent > 0) {
        setTimeout(fetchAndUpdateTaxSavings, 1000);
      }
    } catch (e) {
      setIsLoading(false);
      setSelectedCategoryDetails(null);
      actionType.current = null;
      resetBusinessPercent();
    }
  };
  const businessActionHandler = () => {
    const hasCategory = isCategoryChosen(activeTransaction, selectedCategoryDetails?.categoryId);
    if (activeTransaction && hasCategory) {
      setIsLoading(true);
      animateCardSwipe('animate-cardSwipeRight');
      setTimeout(() => {
        handleMutation();
      }, 800);
    } else {
      setActiveDrawerTransaction(activeTransaction);
    }
  };
  const handleBusinessActionClick = () => {
    actionType.current = ACTION_TYPES.BUSINESS;
    businessActionHandler();
  };
  const handlePersonalActionClick = () => {
    actionType.current = ACTION_TYPES.PERSONAL;
    if (activeTransaction) {
      setIsLoading(true);
      animateCardSwipe('animate-cardSwipeLeft');
      setTimeout(() => {
        handleMutation();
      }, 800);
    }
  };
  const handleNotNowActionClick = () => {
    actionType.current = ACTION_TYPES.NOT_NOW;
    setIsLoading(true);
    setSelectedCategoryDetails(null);
    animateCardSwipe('animate-cardSwipeLeft');
    setTimeout(() => {
      actionType.current = null;
      setActiveTransaction(incomeList?.[0]);
      setIncomeList(incomeList?.slice(1));
    }, 800);
  };
  const handleApproveActionClick = () => {
    actionType.current = ACTION_TYPES.APPROVE;
    businessActionHandler();
  };
  const getActionTypeByBusinessPercent = () => {
    switch (businessPercent) {
      case 0:
        return ACTION_TYPES.PERSONAL;
      case 1:
        return ACTION_TYPES.BUSINESS;
      default:
        return ACTION_TYPES.SPLIT;
    }
  };
  const handleSplitActionClick = () => {
    if (activeCategoryId) setShowSplitModal(true);
    else setShowCategoryTooltip(true);
    trackMixPanel('Act on Split Transaction - Categorization');
  };
  const handleSplitTransactionSave = () => {
    setShowSplitModal(false);
    actionType.current = getActionTypeByBusinessPercent();
    if (actionType.current === ACTION_TYPES.BUSINESS) handleBusinessActionClick();
    else if (actionType.current === ACTION_TYPES.PERSONAL) handlePersonalActionClick();
    else if (activeTransaction) {
      setIsLoading(true);
      animateCardSwipe('animate-cardSwipeRight');
      setTimeout(() => {
        handleMutation();
      }, 800);
    }
  };

  const isNothingLeftToCategorize = () =>
    expenseList.length === 0
    && incomeList.length === 0
    && numOfTotalExpenses - categorizedExpensesCounter === 0
    && numOfTotalIncome - categorizedIncomeCounter === 0;
  const updateActiveListByCategorizationType = () => {
    if (activeCatType === CategorizationTypes.expense) setActiveList(expenseList);
    else if (activeCatType === CategorizationTypes.income) setActiveList(incomeList);
  };
  useEffect(() => {
    if (showSplitModal && activeCategoryId) {
      handleSplitTransactionSave();
    }
  }, [businessPercent]);

  useEffect(() => {
    if (expenseTransactionsList) setExpenseList(expenseTransactionsList);
    if (incomeTransactionsList) setIncomeList(incomeTransactionsList);
  }, [expenseTransactionsList, incomeTransactionsList]);

  useEffect(() => {
    if (selectedCategoryDetails?.categoryId) setShowCategoryTooltip(false);
    if (
      (actionType.current === ACTION_TYPES.BUSINESS || actionType.current === ACTION_TYPES.APPROVE)
      && selectedCategoryDetails?.categoryId
    ) {
      handleBusinessActionClick();
    }
  }, [selectedCategoryDetails]);

  useEffect(() => {
    updateActiveListByCategorizationType();
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
    const shouldReFetchByExpense = activeCatType === CategorizationTypes.expense
      && expenseList.length === 0
      && categorizedExpensesCounter < numOfTotalExpenses;
    const shouldReFetchByIncome = activeCatType === CategorizationTypes.income
      && incomeList.length === 0
      && categorizedIncomeCounter < numOfTotalIncome;
    if (shouldReFetchByExpense || shouldReFetchByIncome) {
      setTimeout(() => {
        fetchUncategorizedData();
      }, 500);
    }
  }, [expenseList, incomeList]);
  useEffect(() => {
    updateActiveListByCategorizationType();
  }, [activeCatType]);
  useEffect(() => {
    if (isNothingLeftToCategorize() || (isIncomeEmpty && !isExpenseEmpty)) {
      setActiveCatType(CategorizationTypes.expense);
      setActiveTransaction(expenseList?.[0]);
    } else if (isExpenseEmpty && !isIncomeEmpty && showIncomeList) {
      setActiveCatType(CategorizationTypes.income);
      setActiveTransaction(incomeList?.[0]);
    }
  }, [activeList]);

  const handleSyncYourCard = () => {
    refetchLogComponent();
    setShowWarningCategoryModal(false);
    if (linkedAccounts.length === 0) {
      setShowSyncCardModal(true);
      navigate(`/${VIEWS_MAP.TRANSACTIONS}`);
    } else if (linkedAccounts.length === 1) {
      setSelectedTransactionTab(TransactionTabs.LINKED_CREDIT_CARDS);
      navigate(`/${VIEWS_MAP.TRANSACTIONS}`);
    }
  };

  const handleSetActiveCatType = (newCatType: CategorizationTypes.expense | CategorizationTypes.income) => {
    setActiveCatType(newCatType);
    if (newCatType === CategorizationTypes.expense) {
      setActiveTransaction(expenseList?.[0]);
    } else if (newCatType === CategorizationTypes.income) {
      setActiveTransaction(incomeList?.[0]);
    }
  };

  return (
    <div className='flex h-full'>
      {showSplitModal && (
        <WidgetModal onClose={() => setShowSplitModal(false)}>
          <WidgetSplitTransaction
            transaction={activeTransaction as LiliTransaction}
            isDisabled={!activeCategoryId}
            onSaveClick={setBusinessPercent}
          />
        </WidgetModal>
      )}
      <div className='flex flex-col items-center justify-between'>
        {showIncomeList && (
          <CategorizationTypeSwitcher
            isDisabled={isLoading || isLoadingMoreUncategorized || (isExpenseEmpty && isIncomeEmpty)}
            activeCatType={activeCatType}
            numOfExpenses={numOfTotalExpenses - categorizedExpensesCounter}
            numOfIncome={numOfTotalIncome - categorizedIncomeCounter}
            onClick={handleSetActiveCatType}
          />
        )}
        {!showEmptyState && (
          <div className='relative h-[222px]'>
            {isLoadingMoreUncategorized && <Spinner spinnerColor={SpinnerColor.Blue} height='2rem' width='2rem' />}
            {!isLoadingMoreUncategorized
              && activeList?.map((transaction: ITransaction, idx: number) => {
                if (idx >= 2) return null;
                const transactionCategory = getCategoryForTransaction(
                  transaction,
                  activeCatType === CategorizationTypes.expense ? expenseCategories : incomeCategories,
                  idx === 0 ? activeCategoryId : '',
                );
                return (
                  <CategorizationCard
                    key={transaction?.transactionId}
                    transaction={transaction}
                    transactionCategory={transactionCategory}
                    cardRef={!idx ? firstCard : idx === 1 ? secondCard : null}
                    className={`z-${20 - idx}`}
                    showCategoryTooltip={showCategoryTooltip}
                  />
                );
              })}
          </div>
        )}
        {!isLoadingMoreUncategorized && showEmptyState && (
          <CategorizationAllDone
            categorizationType={activeCatType}
            isAllCategorized={isExpenseEmpty && isIncomeEmpty}
          />
        )}
        {!showEmptyState && activeTransaction && isExpensesTabActive && (
          <CategorizationBusinessActions
            isDisabled={isLoading}
            className='mt-[24px]'
            onBusinessActionClick={handleBusinessActionClick}
            onPersonalActionClick={handlePersonalActionClick}
            onSplitActionClick={handleSplitActionClick}
          />
        )}
        {!showEmptyState && activeTransaction && isIncomeTabActive && (
          <CategorizationPersonalActions
            isDisabled={isLoading}
            className='mt-[24px] w-[160px] mx-auto'
            onNotNowClick={handleNotNowActionClick}
            onApproveClick={handleApproveActionClick}
          />
        )}
        {showWarningCategoryModal
          && shouldShowSyncCardFeature
          && !(appComponentLog?.doNotShowAgain || false)
          && !isLoadingAppComponent
          && linkedAccounts.length > 0 && (
            <CategorizeCreditCardWarningModalDashboard
              onCanceled={() => setShowWarningCategoryModal(false)}
              onFinished={handleSyncYourCard}
              multipleAccounts={linkedAccounts.length > 1}
              transactionDetails={previousActiveTransactionDetails.current || ''}
            />
        )}
        {showWarningCategoryModal
          && shouldShowSyncCardFeature
          && !(appComponentLog?.doNotShowAgain || false)
          && !isLoadingAppComponent
          && linkedAccounts.length === 0 && (
            <CategorizeCreditCardWarningModal
              onCanceled={() => setShowWarningCategoryModal(false)}
              onFinished={handleSyncYourCard}
              multipleAccounts={false}
              transactionDetails={previousActiveTransactionDetails.current || ''}
            />
        )}
      </div>
    </div>
  );
};
